import React from 'react';
import styled, { useTheme } from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import Button from '../../elements/Button';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableBody, TableCell } from '../../Table';
import { Table, TableHead, TableRow, TableHeader } from '../../Table';
import { TableWrapper } from '../../Table';
import { CompanyResponse, editLearningPlans } from '../../../api/admin';
import { SmallTooltip } from '../../Tooltip';
import LearningPlanEditableCell from '../Cells/LearningPlanEditableCell';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import { toast } from 'react-toastify';
import { Dropdown } from '../../elements/Dropdown';
import useAuth from '../../../hooks/use-auth';
import { useMutation } from 'react-query';
import { getUnitId, LearningPlanItem } from '../Cells/LearningPlanCell';
import { Prompt } from 'react-router-dom';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  min-height: 80vh;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

// using this as an "option" for react-select, hence the "value" and "label" fields
export type EditableLearningPlanItem = {
  learnerId: number; // duplicating learner id here so each item is self-contained
  label: string;
  shortLabel: string;
  value: string; // identifier for the item
} & LearningPlanItem;

// used for the bulk action dropdown
type RawUnitOptionType = {
  type: LEARNING_PLAN_UNIT_TYPE;
  id: number; // Module contentId or project projectId
  slug: string; // Keep slug for reference
  dropdownLabel: string;
};

export type LearnerWithPlan = {
  id: number;
  name: string;
  email: string;
  originalLearningPlan: EditableLearningPlanItem[];
  newLearningPlan: EditableLearningPlanItem[];
};

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  button {
    margin: 0;
    width: 120px;
  }

  > div:first-child {
    width: 500px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export function getModuleDropdownLabel(
  module: CompanyResponse['modules'][number]
) {
  return `[Module] [${module.companyName ?? 'Public'}] ${module.contentTitle}${
    module.isDeprecated ? ' (Deprecated)' : ''
  }${module.isDraft ? ' (Draft)' : ''}`;
}

export function getProjectDropdownLabel(
  project: CompanyResponse['projects'][number]
) {
  return `[${project.topic} Project] [${project.companyName ?? 'Public'}] ${
    project.title
  }${project.isDraft ? ' (Draft)' : ''}`;
}

function mapLearningPlanItem(
  unit: LearningPlanItem,
  learnerId: number
): EditableLearningPlanItem {
  const label =
    unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? `[Module] ${unit.companyName ?? 'Public'} ${unit.content.title}`
      : `[${unit.topic} Project] ${unit.companyName ?? 'Public'} ${unit.title}`;
  const shortLabel =
    unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? unit.content.title
      : unit.title;
  const value = `${learnerId}-${unit.type}-${
    unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? unit.content.slug
      : unit.slug
  }`;

  return {
    ...unit,
    learnerId,
    label,
    shortLabel,
    value,
  };
}

function isUnitDraftOrDeprecated(
  unit: RawUnitOptionType | EditableLearningPlanItem,
  modules: CompanyResponse['modules'],
  projects: CompanyResponse['projects']
): boolean {
  if ('learnerId' in unit) {
    if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      if ('content' in unit && unit.content && 'id' in unit.content) {
        return !!modules.find(
          (m) =>
            m.contentId === unit.content.id && (m.isDraft || m.isDeprecated)
        );
      }
    } else if (unit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
      if ('projectId' in unit) {
        return !!projects.find(
          (p) => p.projectId === unit.projectId && p.isDraft
        );
      }
    }
    return false;
  }

  if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
    return !!modules.find(
      (m) => m.contentId === unit.id && (m.isDraft || m.isDeprecated)
    );
  }
  return !!projects.find((p) => p.projectId === unit.id && p.isDraft);
}

function createUnitFromTemplate(
  unit: RawUnitOptionType,
  learnerId: number,
  modules: CompanyResponse['modules'],
  projects: CompanyResponse['projects']
): EditableLearningPlanItem | null {
  if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
    const moduleDetails = modules.find((m) => m.contentId === unit.id);
    if (!moduleDetails) return null;

    return {
      type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
      learnerId,
      content: {
        id: moduleDetails.contentId,
        slug: moduleDetails.contentSlug,
        title: moduleDetails.contentTitle,
      },
      topic: moduleDetails.topic,
      topicIconUrl: moduleDetails.topicIconUrl,
      coreLessonsCount: moduleDetails.coreActivitiesCount,
      coreSubmittedLessonsCount: 0,
      stretchLessonsCount: moduleDetails.stretchActivitiesCount,
      stretchSubmittedLessonsCount: 0,
      label: getModuleDropdownLabel(moduleDetails),
      shortLabel: moduleDetails.contentTitle,
      value: `${learnerId}-${LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE}-${moduleDetails.contentSlug}`,
      companyName: moduleDetails.companyName,
    };
  } else if (unit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    const projectDetails = projects.find((p) => p.projectId === unit.id);
    if (!projectDetails) return null;

    return {
      type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
      learnerId,
      slug: projectDetails.slug,
      projectId: projectDetails.projectId,
      topic: projectDetails.topic,
      topicIconUrl: projectDetails.topicIconUrl,
      title: projectDetails.title,
      exercisesCount: projectDetails.exercisesCount,
      submittedExercisesCount: 0,
      label: getProjectDropdownLabel(projectDetails),
      shortLabel: projectDetails.title,
      value: `${learnerId}-${LEARNING_PLAN_UNIT_TYPE.PROJECT}-${projectDetails.slug}`,
      companyName: projectDetails.companyName,
    };
  }
  return null;
}

function unitExistsInPlan(
  unit: RawUnitOptionType,
  learningPlan: EditableLearningPlanItem[]
): boolean {
  return learningPlan.some((existingUnit) => {
    if (existingUnit.type !== unit.type) return false;

    return existingUnit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? existingUnit.content.id === unit.id
      : existingUnit.projectId === unit.id;
  });
}

function havePlansChanged(
  oldPlan: EditableLearningPlanItem[],
  newPlan: EditableLearningPlanItem[],
  learnerId: number
): boolean {
  if (oldPlan.length !== newPlan.length) {
    return true;
  }

  return newPlan.some(
    (unit, index) =>
      getUnitId({ item: unit, learnerId }) !==
      getUnitId({ item: oldPlan[index], learnerId })
  );
}

function detectPlanChanges(
  oldPlan: EditableLearningPlanItem[],
  newPlan: EditableLearningPlanItem[],
  learnerId: number
) {
  const additions = newPlan.filter(
    (unit) =>
      !oldPlan.find(
        (l) =>
          getUnitId({ item: l, learnerId }) ===
          getUnitId({ item: unit, learnerId })
      )
  );

  const removals = oldPlan.filter(
    (unit) =>
      !newPlan.find(
        (l) =>
          getUnitId({ item: l, learnerId }) ===
          getUnitId({ item: unit, learnerId })
      )
  );

  const commonItems = newPlan.filter((newItem) =>
    oldPlan.some(
      (oldItem) =>
        getUnitId({ item: oldItem, learnerId }) ===
        getUnitId({ item: newItem, learnerId })
    )
  );

  const oldItemIds = oldPlan
    .filter((item) =>
      commonItems.some(
        (common) =>
          getUnitId({ item: common, learnerId }) ===
          getUnitId({ item, learnerId })
      )
    )
    .map((item) => getUnitId({ item, learnerId }));

  const newItemIds = commonItems.map((item) => getUnitId({ item, learnerId }));

  const hasOrderChanged =
    oldItemIds.length > 0 &&
    !oldItemIds.every((id, index) => id === newItemIds[index]);

  return {
    additions,
    removals,
    hasOrderChanged,
    hasChanges: additions.length > 0 || removals.length > 0 || hasOrderChanged,
  };
}

function getGenericPlanOptions(
  modules: CompanyResponse['modules'],
  projects: CompanyResponse['projects']
): RawUnitOptionType[] {
  return [
    ...modules.map((m) => ({
      type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
      id: m.contentId,
      slug: m.contentSlug,
      dropdownLabel: getModuleDropdownLabel(m),
    })),
    ...projects.map((p) => ({
      type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
      id: p.projectId,
      slug: p.slug,
      dropdownLabel: getProjectDropdownLabel(p),
    })),
  ];
}

const BulkActionWidget = ({
  modules,
  projects,
  onAddUnit,
  onRemoveUnit,
}: {
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  onAddUnit: (unit: RawUnitOptionType) => void;
  onRemoveUnit: (unit: RawUnitOptionType) => void;
}) => {
  const options = getGenericPlanOptions(modules, projects);
  const [selectedOption, setSelectedOption] =
    React.useState<RawUnitOptionType | null>(null);

  const onSelectOption = (option: RawUnitOptionType) => {
    if (isUnitDraftOrDeprecated(option, modules, projects)) {
      toast.error(
        'You cannot add a draft or deprecated unit to the learning plan'
      );
      return;
    }
    setSelectedOption(option);
  };

  return (
    <ActionRow>
      <Dropdown
        options={options}
        onChange={onSelectOption}
        value={selectedOption}
        isValidNewOption={() => false}
        menuPortalTarget={document.body}
        placeholder="Select a unit to add/remove for all learners"
        formatOptionLabel={(option) => option.dropdownLabel}
        getOptionValue={(option) => `${option.type}-${option.id}`}
        getOptionLabel={(option) => option.dropdownLabel}
      />
      <ButtonsContainer>
        <Button
          label="Add"
          type="secondary"
          faIcon="fa-solid fa-plus"
          showIconBeforeLabel
          isDisabled={!selectedOption}
          onClick={() => {
            if (selectedOption) {
              onAddUnit(selectedOption);
              setSelectedOption(null);
            }
          }}
        />
        <Button
          label="Remove"
          type="secondary"
          faIcon="fa-solid fa-minus"
          showIconBeforeLabel
          isDisabled={!selectedOption}
          onClick={() => {
            if (selectedOption) {
              onRemoveUnit(selectedOption);
              setSelectedOption(null);
            }
          }}
        />
      </ButtonsContainer>
    </ActionRow>
  );
};

const columnHelper = createColumnHelper<LearnerWithPlan>();

export default function EditLearnersLearningPlanModal({
  isOpen,
  onClose,
  companyId,
  selectedLearnerIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedLearnerIds: number[];
}) {
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
    isFetching: isFetchingCompany,
  } = useGetCompany(companyId);
  const [newLearners, setNewLearners] = React.useState<LearnerWithPlan[]>([]);
  const { getTokenSilently } = useAuth();

  const editPlanMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      return editLearningPlans(token, {
        companyId,
        learners: newLearners.map((learner) => ({
          id: learner.id,
          learningPlan: learner.newLearningPlan.map((unit) => ({
            type: unit.type,
            slug:
              unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
                ? unit.content.slug
                : unit.slug,
            reasoning: '',
          })),
        })),
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learning plans updated successfully');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update learning plans'
      );
    },
  });

  const hasChanges = newLearners.some((learner) =>
    havePlansChanged(
      learner.originalLearningPlan,
      learner.newLearningPlan,
      learner.id
    )
  );

  const initializeLearnersData = React.useCallback(() => {
    if (isFetchingCompany) {
      return;
    }

    if (isOpen && hasChanges) {
      return;
    }

    const learners: LearnerWithPlan[] = selectedLearnerIds
      .map((id) => data?.learners.find((learner) => learner.id === id))
      .filter(
        (learner): learner is CompanyResponse['learners'][number] => !!learner
      )
      .map((learner) => {
        return {
          id: learner.id,
          name: learner.name,
          email: learner.email,
          originalLearningPlan:
            learner.learningPlan?.map((unit) =>
              mapLearningPlanItem(unit, learner.id)
            ) ?? [],
          newLearningPlan:
            learner.learningPlan?.map((unit) =>
              mapLearningPlanItem(unit, learner.id)
            ) ?? [],
        };
      });
    setNewLearners(learners);
  }, [data, selectedLearnerIds, isFetchingCompany, isOpen, hasChanges]);

  React.useEffect(() => {
    console.log('initializeLearnersData');
    initializeLearnersData();
  }, [initializeLearnersData]);

  const handleUpdateData = (value: {
    learnerId: number;
    newLearningPlan: EditableLearningPlanItem[];
  }) => {
    const oldPlan = newLearners.find(
      (l) => l.id === value.learnerId
    )?.originalLearningPlan;
    const newPlan = value.newLearningPlan;

    if (!oldPlan) return;

    const additions = newPlan.filter(
      (unit) =>
        !oldPlan.find(
          (l) =>
            getUnitId({ item: l, learnerId: unit.learnerId }) ===
            getUnitId({ item: unit, learnerId: unit.learnerId })
        )
    );

    const hasDraftOrDeprecatedAddition = additions.some((unit) =>
      isUnitDraftOrDeprecated(unit, data?.modules ?? [], data?.projects ?? [])
    );

    if (hasDraftOrDeprecatedAddition) {
      toast.error(
        'You cannot add a draft or deprecated unit to the learning plan'
      );
      return;
    }

    const updatedLearners = newLearners.map((learner) => {
      if (learner.id === value.learnerId) {
        return {
          ...learner,
          newLearningPlan: value.newLearningPlan.map((unit) => ({
            ...unit,
            isNew: !oldPlan.find(
              (l) =>
                getUnitId({ item: l, learnerId: learner.id }) ===
                getUnitId({ item: unit, learnerId: learner.id })
            ),
          })),
        };
      }
      return learner;
    });
    setNewLearners(updatedLearners);
  };

  const onBulkAddUnit = (unit: RawUnitOptionType) => {
    const updatedLearners = newLearners.map((learner) => {
      if (unitExistsInPlan(unit, learner.newLearningPlan)) {
        return learner;
      }

      const originalUnit = learner.originalLearningPlan.find((original) => {
        if (original.type !== unit.type) return false;

        return original.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
          ? original.content.id === unit.id
          : original.projectId === unit.id;
      });

      if (originalUnit) {
        return {
          ...learner,
          newLearningPlan: [...learner.newLearningPlan, originalUnit],
        };
      }

      const newUnit = createUnitFromTemplate(
        unit,
        learner.id,
        data?.modules ?? [],
        data?.projects ?? []
      );
      if (!newUnit) return learner;

      return {
        ...learner,
        newLearningPlan: [...learner.newLearningPlan, newUnit],
      };
    });

    setNewLearners(updatedLearners);
  };

  const onBulkRemoveUnit = (unit: RawUnitOptionType) => {
    setNewLearners(
      newLearners.map((learner) => ({
        ...learner,
        newLearningPlan: learner.newLearningPlan.filter((existingUnit) => {
          if (unit.type !== existingUnit.type) return true;

          if (
            unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE &&
            existingUnit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
          ) {
            return existingUnit.content.id !== unit.id;
          }

          if (
            unit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT &&
            existingUnit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT
          ) {
            return existingUnit.projectId !== unit.id;
          }

          return true;
        }),
      }))
    );
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Prompt
        when={hasChanges && isOpen}
        message="You have unsaved changes. Are you sure you want to leave this page?"
      />
      <Row>
        <Header>Edit {data?.name} Learning Plans</Header>
        <CloseButton onClick={handleClose} />
      </Row>
      <Content>
        <PSmall style={{ marginTop: '32px' }}>
          You have selected {selectedLearnerIds.length} learners to edit their
          learning plans.
        </PSmall>
        <BulkActionWidget
          modules={data?.modules ?? []}
          projects={data?.projects ?? []}
          onAddUnit={onBulkAddUnit}
          onRemoveUnit={onBulkRemoveUnit}
        />
        <EditLearnersTable
          data={newLearners}
          modules={data?.modules ?? []}
          projects={data?.projects ?? []}
          onUpdateData={handleUpdateData}
        />
        <Row>
          <Button
            label="Save Learning Plans"
            showIconBeforeLabel
            faIcon="fa-solid fa-check"
            type="primary"
            isDisabled={!hasChanges}
            onClick={() => {
              editPlanMutation.mutate();
            }}
            isLoading={editPlanMutation.isLoading || isLoadingCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}

function PlanChangeSummary({
  oldPlan,
  newPlan,
  learnerId,
}: {
  oldPlan: EditableLearningPlanItem[];
  newPlan: EditableLearningPlanItem[];
  learnerId: number;
}): JSX.Element {
  const { additions, removals, hasOrderChanged, hasChanges } =
    detectPlanChanges(oldPlan, newPlan, learnerId);
  const theme = useTheme();

  if (!hasChanges) {
    return <div>No changes</div>;
  }

  const getTitle = (unit: EditableLearningPlanItem) => {
    return unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? unit.content.title
      : unit.title;
  };

  const tooltipId = `changes-tooltip-${learnerId}`;

  return (
    <div style={{ width: 'max-content' }}>
      <span
        data-tooltip-id={tooltipId}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <i
          className="fa-solid fa-circle-exclamation"
          style={{ marginRight: '8px', color: theme.colors.secondary }}
        ></i>
        Changes detected
      </span>
      <SmallTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        {additions.length > 0 && (
          <div>
            <b>Added:</b>{' '}
            {additions.map((unit) => `"${getTitle(unit)}"`).join(', ')}
          </div>
        )}
        {removals.length > 0 && (
          <div>
            <b>Removed:</b>{' '}
            {removals.map((unit) => `"${getTitle(unit)}"`).join(', ')}
          </div>
        )}
        {hasOrderChanged && (
          <div>
            <b>Reordered:</b> Items have been rearranged
          </div>
        )}
      </SmallTooltip>
    </div>
  );
}

const TWrapper = styled(TableWrapper)`
  overflow-x: auto;
  table {
    min-width: 70vw;
  }
`;

const EditLearnersTable = ({
  data,
  modules,
  projects,
  onUpdateData,
}: {
  data: LearnerWithPlan[];
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  onUpdateData: (value: {
    learnerId: number;
    newLearningPlan: EditableLearningPlanItem[];
  }) => void;
}) => {
  const columns = [
    columnHelper.display({
      id: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <>
            <div className={`learner-${row.original.id}`}>
              {row.original.name ?? 'N/A'}
            </div>
            <SmallTooltip anchorSelect={`.learner-${row.original.id}`}>
              {row.original.email}
            </SmallTooltip>
          </>
        );
      },
      meta: {
        styles: {
          minWidth: '250px',
          maxWidth: '250px',
        },
      },
    }),
    columnHelper.display({
      id: 'learningPlan',
      header: 'Learning Plan',
      cell: ({ row }) => {
        return (
          <LearningPlanEditableCell
            modules={modules}
            projects={projects}
            options={getLearnerPlanOptions(modules, projects, row.original)}
            learnerWithLearningPlan={row.original}
            onChange={(newLearningPlan) => {
              console.log('newLearningPlan', newLearningPlan);
              onUpdateData({
                learnerId: row.original.id,
                newLearningPlan,
              });
            }}
          />
        );
      },
      meta: {
        styles: {
          minWidth: '800px',
          maxWidth: '1000px',
          overflowX: 'auto',
          width: '100%',
          padding: '4px 12px',
          scrollbarWidth: 'none',
        },
      },
    }),
    columnHelper.display({
      id: 'changesSummary',
      header: 'Changes?',
      cell: ({ row }) => {
        return (
          <PlanChangeSummary
            learnerId={row.original.id}
            oldPlan={row.original.originalLearningPlan}
            newPlan={row.original.newLearningPlan}
          />
        );
      },
      meta: {
        styles: {
          minWidth: '200px',
          maxWidth: '200px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          padding: '0 16px',
        },
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  return (
    <TWrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} meta={header.column.columnDef.meta}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} isSelected={row.getIsSelected()}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} meta={cell.column.columnDef.meta}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TWrapper>
  );
};

function getLearnerPlanOptions(
  modules: CompanyResponse['modules'],
  projects: CompanyResponse['projects'],
  learnerWithPlan: LearnerWithPlan
): EditableLearningPlanItem[] {
  const createPlanOption = (
    type: LEARNING_PLAN_UNIT_TYPE,
    item:
      | CompanyResponse['modules'][number]
      | CompanyResponse['projects'][number]
  ): EditableLearningPlanItem => {
    const isModule = type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE;
    const existingItem = learnerWithPlan.originalLearningPlan.find(
      (original) => {
        if (original.type !== type) return false;
        const itemId = isModule
          ? (item as CompanyResponse['modules'][number]).contentId
          : (item as CompanyResponse['projects'][number]).projectId;
        return original.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
          ? original.content.id === itemId
          : original.projectId === itemId;
      }
    );

    if (existingItem) return existingItem;

    if (isModule) {
      const module = item as CompanyResponse['modules'][number];
      const unit: EditableLearningPlanItem = {
        type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
        learnerId: learnerWithPlan.id,
        content: {
          id: module.contentId,
          slug: module.contentSlug,
          title: module.contentTitle,
        },
        topic: module.topic,
        topicIconUrl: module.topicIconUrl,
        coreLessonsCount: module.coreActivitiesCount,
        coreSubmittedLessonsCount: 0,
        stretchLessonsCount: module.stretchActivitiesCount,
        stretchSubmittedLessonsCount: 0,
        label: getModuleDropdownLabel(module),
        shortLabel: module.contentTitle,
        value: `${learnerWithPlan.id}-${LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE}-${module.contentSlug}`,
        companyName: module.companyName,
      };
      return unit;
    } else {
      const project = item as CompanyResponse['projects'][number];
      const unit: EditableLearningPlanItem = {
        type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
        learnerId: learnerWithPlan.id,
        slug: project.slug,
        projectId: project.projectId,
        topic: project.topic,
        topicIconUrl: project.topicIconUrl,
        title: project.title,
        exercisesCount: project.exercisesCount,
        submittedExercisesCount: 0,
        label: getProjectDropdownLabel(project),
        shortLabel: project.title,
        value: `${learnerWithPlan.id}-${LEARNING_PLAN_UNIT_TYPE.PROJECT}-${project.slug}`,
        companyName: project.companyName,
      };
      return unit;
    }
  };

  const moduleOptions = modules.map((module) =>
    createPlanOption(LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE, module)
  );

  const projectOptions = projects.map((project) =>
    createPlanOption(LEARNING_PLAN_UNIT_TYPE.PROJECT, project)
  );

  return [...moduleOptions, ...projectOptions];
}
